/**
 * WEB-2057 — подсказки АВТОРУ внутри превью конструктора.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МАЛЕНЬКИЙ СЛОВАРЬ. Эти восемь надписей рисует общий рендерер
 * блоков — тот самый, который рисует и публичную воронку покупателю. Показаны
 * они ТОЛЬКО под `isEditorPreview` (на публичной странице такой ветки нет), но
 * импорт словаря статический, и покупателю ехал весь словарь конструктора —
 * 60 КБ несжатых в первом экране. Дублировать тексты нельзя, грузить словарь
 * лениво из синхронного рендера — тоже; поэтому надписи вынесены в свой файл
 * на полтора килобайта, и рендерер импортирует его.
 *
 * Тексты перенесены ДОСЛОВНО из `editor.ts` (обе половины), вид и слова не
 * менялись. Правило «текст кабинета живёт в словаре, парой ru/en» соблюдено:
 * файл зарегистрирован в `dictionaries/index.ts` и проверяется тем же сторожем
 * `__tests__/cabinet-strings-locale.web1874.test.ts`.
 *
 * Сторож первого экрана (`findFirstScreenCabinetDictionaries`, WEB-2057)
 * знает про этот файл поимённо, с причиной, и у исключения есть вторая
 * сторона: строка про файл, которого в сборке нет, красная.
 */
import { defineDictionary } from '../define';

export const authorHintsStrings = defineDictionary(
  {
    redirectBlockTitle: 'Redirect block',
    redirectAutoJump: (seconds: number) =>
      `Auto-redirect in ${seconds}s (disabled in the editor)`,
    autoAdvanceBadge: '→ auto-advance',
    cookieBannerPreviewHint:
      'Cookie banner — press “Show” in the properties panel',
    downsellPopupPreviewHint:
      'Downsell popup — press “Show” in the properties panel',
    // WEB-1906: подсказка АВТОРУ в превью конструктора — на публичной
    // странице такой кнопки не будет вовсе, пока адрес не заполнен.
    storeAutoUrlMissing:
      'Store link is not set in the project settings — this button will not be shown',
    // Ревизия пачки 19: подсказка АВТОРУ у «закрыть встроенное окно». Превью
    // рисует кнопку всегда (WEB-1212), а публично в обычном браузере её нет,
    // пока не задана своя ссылка, — превью не имеет права обещать больше.
    closeWebviewOnlyInApp:
      'This link is not accepted — its scheme differs from this project’s configured app scheme (or it is a service marker); outside the app the close button will not appear',
    // Ревизия пачки 19: та же дверь у кнопки возврата в приложение — а
    // сигнала автору не было вовсе. Ссылка задана и не принята → говорим
    // словами, к чему кнопка вернётся.
    returnToAppLinkNotAllowed:
      'This link is not accepted — its scheme differs from this project’s configured app scheme (or it is a service marker); the button falls back to the standard return to the app',
    // Пустая разбивка цены: на бою блок молчит, в холсте редактора обязан быть
    // виден и выделяем (шрам WEB-846 «блок-невидимка»).
    priceAppearsWithPlan:
      'The price appears once the screen has a plan selector or a price block',
    // WEB-3436: пустой рейтинг «Звёзд отзывов» скрывает блок у покупателя
    // (решение владельца 16.10) — в превью автор видит плашку вместо блока.
    // Коротко намеренно: словарь едет в первом экране покупателя, а вес
    // первого экрана под храповиком (WEB-1988), запаса на длинную фразу нет.
    reviewStarsRatingEmpty: 'Rating is empty — buyers will not see this block',
    // Конфликты цен собирает `components/editor/pricingGate.ts` — он общий с
    // рантаймом пейволла (правила наличия цены), а сами тексты читает АВТОР в
    // гейте публикации.
    priceConflictButtonVsPlanSelector:
      'The pay button sets its own price, but the screen also has a plan ' +
      'selector — the selected plan is charged and the button price is ignored. ' +
      'Remove the price from the button or delete the plan selector.',
    priceConflictPriceVsButton:
      'The price block shows one price while the pay button charges another — ' +
      'they do not match. Make both prices the same or remove the price from ' +
      'one of the blocks.',
    priceConflictTwoPlanSelectors:
      'The screen has several plan selectors — only the first one goes into the ' +
      'payment. Leave a single plan selector on the screen.',
    // Задание stylepack/feedback-reveal: подсказка с появлением после ответа
    // в превью видна всегда — пометка говорит автору, что покупатель увидит её
    // только после выбора.
    microFeedbackRevealBadge: 'appears after an answer',
  },
  {
    redirectBlockTitle: 'Redirect-блок',
    redirectAutoJump: (seconds: number) =>
      `Автопереход через ${seconds} сек (в редакторе отключён)`,
    autoAdvanceBadge: '→ авто-переход',
    cookieBannerPreviewHint:
      'Cookie-баннер — нажмите «Показать» в панели свойств',
    downsellPopupPreviewHint:
      'Попап-даунселл — нажмите «Показать» в панели свойств',
    storeAutoUrlMissing:
      'Адрес магазина не заполнен в настройках проекта — кнопка показана не будет',
    closeWebviewOnlyInApp:
      'Эта ссылка не принята — её схема не совпадает с настроенной схемой приложения этого проекта (или это служебный маркер); вне приложения кнопка закрытия не появится',
    returnToAppLinkNotAllowed:
      'Эта ссылка не принята — её схема не совпадает с настроенной схемой приложения этого проекта (или это служебный маркер); кнопка вернётся к штатному возврату в приложение',
    priceAppearsWithPlan:
      'Цена появится, когда на экране будет тариф или блок цены',
    reviewStarsRatingEmpty: 'Рейтинг не заполнен — покупатель блок не увидит',
    priceConflictButtonVsPlanSelector:
      'Кнопка оплаты задаёт свою цену, но на экране есть «Выбор тарифа» — ' +
      'при оплате спишется выбранный тариф, цена кнопки игнорируется. ' +
      'Уберите цену у кнопки или удалите «Выбор тарифа».',
    priceConflictPriceVsButton:
      'Блок «Цена» показывает одну цену, а кнопка оплаты спишет ' +
      'другую — их цены не совпадают. Приведите обе цены к одной ' +
      'или уберите цену у одного из блоков.',
    priceConflictTwoPlanSelectors:
      'На экране несколько блоков «Выбор тарифа» — в оплату уходит только ' +
      'первый. Оставьте один «Выбор тарифа» на экране.',
    microFeedbackRevealBadge: 'появится после ответа',
  },
);
